import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	getMeetgoReservationDetailInfoAction,
	changeValue,
	getWalkInReservationSlot,
	getReservationList,
	getHomeServiceCheckInfoAction,
	getCarCenterLocationInfo,
	getVideosReservationSlot,
} from 'app/store/modules/meetgo/reservation';
import { checkAvailabilityReservation, postMeetgoWalkInReservation } from 'app/api';
import MeetgoCenter from 'app/libs/meetgo';
import dayjs from 'app/libs/dayjs';
import { loggingException } from 'app/libs/sentry';
import AlignBox from 'app/components/common/AlignBox/AlignBox';
import LayerComm from 'app/components/common/LayerComm/LayerComm';
import qs from 'query-string';
import { Modal, Button } from '@encarpkg/design';

export default function useMeetgoReservationInfo() {
	const { dateSlotList, selectedDate, selectedTime, detailInfo, isDetailInfoExist, selectedEndTime, verifiedPhone, reservationList } = useSelector(
		(state) => state.meetgo.reservation,
		shallowEqual,
	);

	const history = useHistory();
	const { reservationProcess } = qs.parse(history.location.search);

	const meetgoCenter = useMemo(() => {
		const meetgoCenterInstance = new MeetgoCenter();
		meetgoCenterInstance.changeCenterPhoneNumber(detailInfo?.vehicleSafetyPhoneNumber ?? detailInfo?.centerPhoneNumber);
		meetgoCenterInstance.changeCenterCode(detailInfo?.centerCode);
		return meetgoCenterInstance;
	}, [detailInfo]);

	const postApplyReservaion = async (selectedCarId) => {
		// 예약 신청

		const param = {
			vehicleId: selectedCarId,
			customerUserId: detailInfo?.customerUserId,
			customerMobilePhoneNumber: detailInfo?.customerMobilePhoneNumber,
			customerName: detailInfo?.customerName,
			useDate: detailInfo?.useDate,
			useStartTime: detailInfo?.useStartTime,
			useEndTime: detailInfo?.useEndTime,
			// 전문가 동행 여부 - 원 예약값을 승계하여 추가 예약에도 동일하게 전달
			expertAccompanyFlag: detailInfo?.expertAccompanyFlag,
		};
		try {
			await postMeetgoWalkInReservation(param);
		} catch (e) {
			throw e;
		}
	};

	const getMoreReservationAvailabilityStatus = useCallback(async () => {
		// 상태값 체크
		try {
			if (!detailInfo) {
				return null;
			}
			const { data } = await checkAvailabilityReservation(detailInfo?.salesAgentReservationId);

			return data;
		} catch (error) {
			console.error(error);
			loggingException(error);
			return null;
		}
	}, [detailInfo]);

	const dispatch = useDispatch();

	const getReservationDetailData = useCallback((reservationId) => dispatch(getMeetgoReservationDetailInfoAction(reservationId)), [dispatch]);

	const changeComm = useCallback((payload) => dispatch(changeValue(payload)), [dispatch]);

	const getWalkInSlotInfo = useCallback((param) => dispatch(getWalkInReservationSlot(param)), [dispatch]);

	const getVideoSlotInfo = useCallback((param) => dispatch(getVideosReservationSlot(param)), [dispatch]);

	const getReservationListInfo = useCallback((params, signal) => dispatch(getReservationList(params, signal)), [dispatch]);
	const getHomeServiceCheckInfo = useCallback(
		() => dispatch(getHomeServiceCheckInfoAction(detailInfo?.vehicleId)),
		[detailInfo?.vehicleId, dispatch],
	);

	const getCarCenterLocation = useCallback((action, signal) => dispatch(getCarCenterLocationInfo(action, signal)), [dispatch]);

	const reservationBtnClick = useCallback(
		async (layerFn) => {
			const reloadPage = () => {
				window.location.reload();
			};
			const backPage = () => {
				history.goBack();
			};

			try {
				const { data: reservationData } = await getReservationDetailData(detailInfo?.salesAgentReservationId);
				const statusType = reservationData.salesAgentStatusType;

				// 방문예약 상태가 예약확정 상태인 경우
				if (statusType === 'RESERVATION_CONFIRMED') {
					return (
						<Modal
							isOpen
							dimClosable={false}
							onClose={layerFn.reset}
							title="담당자 확인이 필요합니다"
						>
							<p
								style={{
									fontWeight: 500,
									fontSize: '15px',
									color: '#666',
									textAlign: 'center',
								}}
							>
								방문 예약 일시에 차량 2대가 예약되어 있어 변경 시 담당자 확인이 필요합니다.
								<br />
								엔카 믿고거래존에 문의해 주세요.
							</p>
							<AlignBox type="float">
								<Button.V2
									domType="button"
									color="gray"
									onClick={layerFn.reset}
									style={{ marginTop: '30px' }}
								>
									닫기
								</Button.V2>
								<Button.V2
									domType="button"
									color="primary"
									onClick={layerFn.openCall}
									style={{ marginTop: '30px' }}
								>
									전화문의
								</Button.V2>
							</AlignBox>
						</Modal>
					);
				}

				// 방문예약 상태가 예약신청 상태가 아닌경우
				if (statusType !== 'RESERVATION_REQUEST') {
					if (reservationProcess === 'Y') {
						return (
							<Modal
								isOpen
								okText="확인"
								onClickOk={backPage}
								title={
									<>
										방문예약일시 변경에 실패하였습니다
										<br />
										잠시 후 다시 시도해 주세요
									</>
								}
							/>
						);
					}
					return null;
				}

				// 방문예약 상태가 예약신청 상태이나, 방문예약일시가 초과된 경우
				const reservationVisitDate = dayjs(`${detailInfo?.useDate} ${detailInfo?.useStartTime}`);
				if (dayjs().isAfter(reservationVisitDate)) {
					return (
						<Modal
							isOpen
							okText="확인"
							onClickOk={reloadPage}
							title={
								<>
									방문예약일시 변경에 실패하였습니다
									<br />
									잠시 후 다시 시도해 주세요
								</>
							}
						/>
					);
				}

				return true;
			} catch (e) {
				console.error(e);
				loggingException(e);
				return reservationProcess === 'Y' ? (
					<LayerComm
						title="해당 시간에는 방문신청이 불가합니다."
						useSubmitBtn
						onClose={backPage}
						onSubmit={backPage}
					/>
				) : (
					<LayerComm
						title="방문예약일시 변경이 불가능합니다."
						useSubmitBtn
						onClose={reloadPage}
						onSubmit={reloadPage}
					/>
				);
			}
		},
		[detailInfo?.salesAgentReservationId, detailInfo?.useDate, detailInfo?.useStartTime, getReservationDetailData, history, reservationProcess],
	);

	return {
		dateSlotList,
		selectedDate,
		selectedTime,
		selectedEndTime,
		detailInfo,
		isDetailInfoExist,
		reservationList,
		meetgoCenter,
		changeComm,
		getReservationDetailData,
		getWalkInSlotInfo,
		getVideoSlotInfo,
		getReservationListInfo,
		getHomeServiceCheckInfo,
		getCarCenterLocation,
		verifiedPhone,
		reservationBtnClick,
		getMoreReservationAvailabilityStatus,
		postApplyReservaion,
	};
}
